import { ref, uploadBytes, getDownloadURL, deleteObject, listAll } from 'firebase/storage'
import { collection, addDoc, getDocs, doc, updateDoc, deleteDoc, query, where, orderBy, Timestamp, deleteField } from 'firebase/firestore'
import { storage, db, shouldSkipFirestore } from './config'
import { getCurrentUser } from './auth'
import { logger } from '@/utils/logger'
import { errorHandler } from '@/utils/errorHandler'
import { optimizeImage } from '@/utils/imageConverter'

export interface SiteImage {
  id: string
  url: string
  category: ImageCategory
  name: string
  displayTitle?: string
  linkUrl?: string
  uploadedAt: Date
  storagePath: string
}

export type ImageCategory = 'home' | 'about' | 'floating' | 'hero-website'

// Bild hochladen
export const uploadImage = async (
  file: File, 
  category: ImageCategory,
  name: string,
  metadata?: Pick<SiteImage, 'displayTitle' | 'linkUrl'>
): Promise<string> => {
  if (!db) {
    throw new Error('Firestore ist nicht initialisiert')
  }
  
  // Prüfe, ob der Benutzer authentifiziert ist und hole ein frisches Token
  const user = getCurrentUser()
  if (!user) {
    throw new Error('Benutzer ist nicht authentifiziert. Bitte melden Sie sich an.')
  }
  
  // Stelle sicher, dass wir ein frisches Token haben
  try {
    await user.getIdToken(true) // true = force refresh
  } catch (error) {
    logger.error('Fehler beim Aktualisieren des Auth-Tokens', error)
    throw new Error('Authentifizierung fehlgeschlagen. Bitte melden Sie sich erneut an.')
  }
  
  // Wenn kein Name angegeben, verwende den Dateinamen (ohne Extension)
  const imageName = name.trim() || file.name.replace(/\.[^/.]+$/, '')
  
  // Konvertiere Bild zu WebP für bessere Kompression
  let optimizedFile: File
  try {
    optimizedFile = await optimizeImage(file)
    logger.info(`Bild optimiert: ${file.size} bytes → ${optimizedFile.size} bytes (${Math.round((1 - optimizedFile.size / file.size) * 100)}% Einsparung)`)
  } catch (error) {
    logger.warn('Fehler bei Bildoptimierung, verwende Originaldatei', error)
    optimizedFile = file
  }
  
  // Verwende immer .webp Extension nach Optimierung
  const timestamp = Date.now()
  const fileName = `images/${category}/${imageName}-${timestamp}.webp`
  const storageRef = ref(storage, fileName)
  
  await uploadBytes(storageRef, optimizedFile)
  const downloadURL = await getDownloadURL(storageRef)
  
  // Speichere Metadaten in Firestore
  const imageData: Record<string, any> = {
    name: imageName,
    url: downloadURL,
    category,
    storagePath: fileName,
    uploadedAt: Timestamp.now()
  }

  if (metadata?.displayTitle?.trim()) {
    imageData.displayTitle = metadata.displayTitle.trim()
  }

  if (metadata?.linkUrl?.trim()) {
    imageData.linkUrl = metadata.linkUrl.trim()
  }

  await addDoc(collection(db, 'images'), imageData)
  
  return downloadURL
}

// Bild löschen
export const deleteImage = async (imageId: string, storagePath: string): Promise<void> => {
  if (!db) {
    throw new Error('Firestore ist nicht initialisiert')
  }
  
  // Prüfe, ob der Benutzer authentifiziert ist
  const user = getCurrentUser()
  if (!user) {
    throw new Error('Benutzer ist nicht authentifiziert. Bitte melden Sie sich an.')
  }
  
  try {
    // Lösche aus Storage
    const imageRef = ref(storage, storagePath)
    await deleteObject(imageRef)
    
    // Lösche aus Firestore
    await deleteDoc(doc(db, 'images', imageId))
  } catch (error) {
    errorHandler.handle(error, 'deleteImage')
    throw error
  }
}

// Bildnamen aktualisieren
export const updateImageName = async (imageId: string, newName: string): Promise<void> => {
  if (!db) {
    throw new Error('Firestore ist nicht initialisiert')
  }
  
  const user = getCurrentUser()
  if (!user) {
    throw new Error('Benutzer ist nicht authentifiziert. Bitte melden Sie sich an.')
  }
  
  try {
    await updateDoc(doc(db, 'images', imageId), {
      name: newName.trim()
    })
  } catch (error) {
    errorHandler.handle(error, 'updateImageName')
    throw error
  }
}

// Bild-Metadaten aktualisieren
export const updateImageDetails = async (
  imageId: string,
  data: Pick<Partial<SiteImage>, 'displayTitle' | 'linkUrl'>
): Promise<void> => {
  if (!db) {
    throw new Error('Firestore ist nicht initialisiert')
  }

  const user = getCurrentUser()
  if (!user) {
    throw new Error('Benutzer ist nicht authentifiziert. Bitte melden Sie sich an.')
  }

  try {
    await updateDoc(doc(db, 'images', imageId), {
      displayTitle: data.displayTitle?.trim() ? data.displayTitle.trim() : deleteField(),
      linkUrl: data.linkUrl?.trim() ? data.linkUrl.trim() : deleteField()
    })
  } catch (error) {
    errorHandler.handle(error, 'updateImageDetails')
    throw error
  }
}

// Alle Bilder einer Kategorie abrufen
export const getImagesByCategory = async (category: ImageCategory): Promise<SiteImage[]> => {
  if (!db) {
    throw new Error('Firestore ist nicht initialisiert')
  }
  
  // Prüfe, ob der Benutzer authentifiziert ist und hole ein frisches Token
  const user = getCurrentUser()
  if (!user) {
    throw new Error('Benutzer ist nicht authentifiziert. Bitte melden Sie sich an.')
  }
  
  // Stelle sicher, dass wir ein frisches Token haben
  try {
    await user.getIdToken(true) // true = force refresh
  } catch (error) {
    logger.error('Fehler beim Aktualisieren des Auth-Tokens', error)
    throw new Error('Authentifizierung fehlgeschlagen. Bitte melden Sie sich erneut an.')
  }
  
  try {
    // Lade Metadaten aus Firestore
    const q = query(
      collection(db, 'images'),
      where('category', '==', category),
      orderBy('uploadedAt', 'desc')
    )
    const querySnapshot = await getDocs(q)
    
    const images: SiteImage[] = []
    
    for (const docSnapshot of querySnapshot.docs) {
      const data = docSnapshot.data()
      images.push({
        id: docSnapshot.id,
        url: data.url,
        category: data.category,
        name: data.name,
        displayTitle: data.displayTitle,
        linkUrl: data.linkUrl,
        storagePath: data.storagePath,
        uploadedAt: data.uploadedAt?.toDate() || new Date()
      })
    }
    
    return images
  } catch (error) {
    logger.error('Fehler beim Abrufen der Bilder', error)
    // Fallback: Lade aus Storage (für Migration)
    try {
      const folderRef = ref(storage, `images/${category}`)
      const result = await listAll(folderRef)
      
      const images: SiteImage[] = []
      
      for (const itemRef of result.items) {
        try {
          const url = await getDownloadURL(itemRef)
          const name = itemRef.name.split('-').slice(0, -1).join('-')
          images.push({
            id: itemRef.name,
            url,
            category,
            name,
            displayTitle: undefined,
            linkUrl: undefined,
            storagePath: `images/${category}/${itemRef.name}`,
            uploadedAt: new Date()
          })
        } catch (error) {
          logger.error('Fehler beim Abrufen der Bild-URL', error)
        }
      }
      
      return images.sort((a, b) => b.uploadedAt.getTime() - a.uploadedAt.getTime())
    } catch (fallbackError) {
      logger.error('Fehler beim Fallback-Laden', fallbackError)
      return []
    }
  }
}

// Alle Bilder abrufen
export const getAllImages = async (): Promise<SiteImage[]> => {
  const [homeImages, aboutImages, floatingImages, heroWebsiteImages] = await Promise.all([
    getImagesByCategory('home'),
    getImagesByCategory('about'),
    getImagesByCategory('floating'),
    getImagesByCategory('hero-website')
  ])
  
  return [...homeImages, ...aboutImages, ...floatingImages, ...heroWebsiteImages]
}

// Mehrere Bilder löschen (Bulk-Delete)
export const deleteMultipleImages = async (images: SiteImage[]): Promise<void> => {
  const user = getCurrentUser()
  if (!user) {
    throw new Error('Benutzer ist nicht authentifiziert. Bitte melden Sie sich an.')
  }
  
  const deletePromises = images.map(image => deleteImage(image.id, image.storagePath))
  await Promise.all(deletePromises)
}

// Öffentliche Funktion: Bilder einer Kategorie abrufen (ohne Authentifizierung)
// Diese Funktion kann von öffentlichen Seiten verwendet werden
export const getPublicImagesByCategory = async (category: ImageCategory): Promise<SiteImage[]> => {
  // Für Bots/Performance-Tools: Keine Firestore-Abfrage, um Timeouts zu vermeiden
  if (shouldSkipFirestore || !db) {
    logger.debug('Firestore übersprungen - Bilder werden nicht geladen');
    return [];
  }
  
  try {
    // Lade Metadaten aus Firestore (öffentlich lesbar)
    const q = query(
      collection(db, 'images'),
      where('category', '==', category),
      orderBy('uploadedAt', 'desc')
    )
    const querySnapshot = await getDocs(q)
    
    const images: SiteImage[] = []
    
    for (const docSnapshot of querySnapshot.docs) {
      const data = docSnapshot.data()
      images.push({
        id: docSnapshot.id,
        url: data.url,
        category: data.category,
        name: data.name,
        displayTitle: data.displayTitle,
        linkUrl: data.linkUrl,
        storagePath: data.storagePath,
        uploadedAt: data.uploadedAt?.toDate() || new Date()
      })
    }
    
    return images
  } catch (error) {
    logger.error('Fehler beim Abrufen der öffentlichen Bilder', error)
    return []
  }
}

// Öffentliche Funktion: Bild nach Name und Kategorie abrufen
export const getPublicImageByName = async (
  category: ImageCategory,
  name: string
): Promise<SiteImage | null> => {
  // Für Bots/Performance-Tools: Keine Firestore-Abfrage, um Timeouts zu vermeiden
  if (shouldSkipFirestore || !db) {
    logger.debug('Firestore übersprungen - Bild wird nicht geladen');
    return null;
  }
  
  try {
    const q = query(
      collection(db, 'images'),
      where('category', '==', category),
      where('name', '==', name),
      orderBy('uploadedAt', 'desc')
    )
    const querySnapshot = await getDocs(q)
    
    if (querySnapshot.empty) {
      return null
    }
    
    const docSnapshot = querySnapshot.docs[0]
    const data = docSnapshot.data()
    
    return {
      id: docSnapshot.id,
      url: data.url,
      category: data.category,
      name: data.name,
      displayTitle: data.displayTitle,
      linkUrl: data.linkUrl,
      storagePath: data.storagePath,
      uploadedAt: data.uploadedAt?.toDate() || new Date()
    }
  } catch (error) {
    logger.error('Fehler beim Abrufen des Bildes nach Name', error)
    return null
  }
}

/**
 * Lädt ein Bild über ein Image-Element und konvertiert es zu einem Blob
 * Umgeht CORS-Probleme mit Firebase Storage URLs
 * @param url - Die Bild-URL
 * @returns Promise<Blob>
 */
async function loadImageAsBlob(url: string): Promise<Blob> {
  return new Promise((resolve, reject) => {
    const img = new Image()
    
    // Wichtig: crossOrigin muss gesetzt werden für Firebase Storage
    img.crossOrigin = 'anonymous'
    
    img.onload = () => {
      try {
        // Erstelle Canvas und zeichne Bild
        const canvas = document.createElement('canvas')
        canvas.width = img.width
        canvas.height = img.height
        
        const ctx = canvas.getContext('2d')
        if (!ctx) {
          reject(new Error('Canvas-Kontext konnte nicht erstellt werden'))
          return
        }
        
        ctx.drawImage(img, 0, 0)
        
        // Konvertiere Canvas zu Blob
        canvas.toBlob(
          (blob) => {
            if (!blob) {
              reject(new Error('Konvertierung zu Blob fehlgeschlagen'))
              return
            }
            resolve(blob)
          },
          'image/png', // Verwende PNG für verlustfreie Zwischenspeicherung
          1.0 // Maximale Qualität
        )
      } catch (error) {
        reject(error)
      } finally {
        // Cleanup: Revoke Object URL falls verwendet
        URL.revokeObjectURL(img.src)
      }
    }
    
    img.onerror = () => {
      reject(new Error(`Bild konnte nicht geladen werden: ${url}`))
    }
    
    // Lade Bild
    img.src = url
  })
}

// Batch-Konvertierung: Konvertiert alle Bilder zu WebP
export interface BatchConvertResult {
  total: number
  converted: number
  skipped: number
  failed: number
  totalSizeBefore: number
  totalSizeAfter: number
  errors: Array<{ imageId: string; name: string; error: string }>
}

export const batchConvertImagesToWebP = async (
  onProgress?: (current: number, total: number, imageName: string) => void
): Promise<BatchConvertResult> => {
  if (!db) {
    throw new Error('Firestore ist nicht initialisiert')
  }
  
  const user = getCurrentUser()
  if (!user) {
    throw new Error('Benutzer ist nicht authentifiziert. Bitte melden Sie sich an.')
  }

  await user.getIdToken(true)

  const result: BatchConvertResult = {
    total: 0,
    converted: 0,
    skipped: 0,
    failed: 0,
    totalSizeBefore: 0,
    totalSizeAfter: 0,
    errors: []
  }

  try {
    // Lade alle Bilder
    const allImages = await getAllImages()
    result.total = allImages.length

    logger.info(`Starte Batch-Konvertierung von ${result.total} Bildern`)

    for (let i = 0; i < allImages.length; i++) {
      const image = allImages[i]
      
      if (onProgress) {
        onProgress(i + 1, result.total, image.name)
      }

      try {
        // Prüfe, ob bereits WebP
        if (image.storagePath.toLowerCase().endsWith('.webp') || image.url.toLowerCase().includes('.webp')) {
          result.skipped++
          logger.debug(`Bild ${image.name} ist bereits WebP, überspringe`)
          continue
        }

        // Lade Bild über Image-Element (umgeht CORS-Probleme)
        // Firebase Storage URLs mit Token funktionieren mit crossOrigin="anonymous"
        const blob = await loadImageAsBlob(image.url)
        const originalSize = blob.size
        result.totalSizeBefore += originalSize

        // Bestimme MIME-Type aus der Response oder Dateiname
        let mimeType = blob.type || 'image/jpeg'
        if (!mimeType.startsWith('image/') || mimeType === 'image/') {
          // Fallback: Bestimme aus Dateiname
          if (image.storagePath.toLowerCase().endsWith('.png')) {
            mimeType = 'image/png'
          } else if (image.storagePath.toLowerCase().endsWith('.jpg') || image.storagePath.toLowerCase().endsWith('.jpeg')) {
            mimeType = 'image/jpeg'
          } else if (image.storagePath.toLowerCase().endsWith('.gif')) {
            mimeType = 'image/gif'
          } else {
            mimeType = 'image/jpeg' // Sicherer Fallback
          }
        }

        // Erstelle File-Objekt
        const fileName = image.storagePath.split('/').pop() || `image-${Date.now()}`
        const file = new File([blob], fileName, { type: mimeType })

        // Konvertiere zu WebP
        const optimizedFile = await optimizeImage(file)
        const newSize = optimizedFile.size
        result.totalSizeAfter += newSize

        // Erstelle neuen Storage-Pfad
        const imageName = image.name
        const timestamp = Date.now()
        const newStoragePath = `images/${image.category}/${imageName}-${timestamp}.webp`
        const newStorageRef = ref(storage, newStoragePath)

        // Lade konvertiertes Bild hoch
        await uploadBytes(newStorageRef, optimizedFile)
        const newDownloadURL = await getDownloadURL(newStorageRef)

        // Aktualisiere Firestore
        await updateDoc(doc(db, 'images', image.id), {
          url: newDownloadURL,
          storagePath: newStoragePath
        })

        // Lösche altes Bild aus Storage
        try {
          const oldStorageRef = ref(storage, image.storagePath)
          await deleteObject(oldStorageRef)
        } catch (deleteError) {
          logger.warn(`Konnte altes Bild nicht löschen: ${image.storagePath}`, deleteError)
          // Nicht kritisch, weiter machen
        }

        result.converted++
        const savings = Math.round((1 - newSize / originalSize) * 100)
        logger.info(`Bild ${image.name} konvertiert: ${originalSize} → ${newSize} bytes (${savings}% Einsparung)`)
      } catch (error) {
        result.failed++
        const errorMessage = error instanceof Error ? error.message : 'Unbekannter Fehler'
        result.errors.push({
          imageId: image.id,
          name: image.name,
          error: errorMessage
        })
        logger.error(`Fehler bei Konvertierung von ${image.name}:`, error)
      }
    }

    const totalSavings = Math.round((1 - result.totalSizeAfter / result.totalSizeBefore) * 100)
    logger.info(
      `Batch-Konvertierung abgeschlossen: ${result.converted} konvertiert, ${result.skipped} übersprungen, ${result.failed} fehlgeschlagen. ` +
      `Gesamteinsparung: ${(result.totalSizeBefore / 1024 / 1024).toFixed(2)} MB → ${(result.totalSizeAfter / 1024 / 1024).toFixed(2)} MB (${totalSavings}%)`
    )

    return result
  } catch (error) {
    errorHandler.handle(error, 'batchConvertImagesToWebP')
    throw error
  }
}
